import { extractSvgAttributes, getFavoriteComponents, getPropertyOrFallback } from '../MapLevel/MapLevelHelpers';

/**
 * Helper functions for MapPlace component
 */

/**
 * Generates SVG markup string for all place shapes
 * This allows rendering places without React overhead
 * stroke-width is set via CSS (.place class in MapPlace.scss)
 * @param {Array} places - Array of place objects
 * @param {string} placeFill - Fill color for active places
 * @param {string} placeStroke - Stroke color for active places
 * @param {string} unitFill - Fill color for disabled places (matches disabled units)
 * @param {string} unitStroke - Stroke color for disabled places
 * @param {boolean} isPreviewMode - When true, ignore unpublished flag and show all places as published
 * @returns {string} SVG markup string
 */
export const generatePlaceShapesMarkup = (places, placeFill, placeStroke, unitFill, unitStroke, isPreviewMode = false) => {
  if (!places || places.length === 0) return '';

  const shapeStrings = places.map((place) => {
    const { type, attributes, pin, displayMode } = place;
    if (!type || !attributes) return '';

    const isInvisible = isPlaceInvisible(pin);
    const isDisabled = displayMode === 'disabled';
    const isUnpublished = pin?.unpublished === true && !isPreviewMode;

    // Unpublished and disabled places use unit colors from config
    const fill = (isUnpublished || isDisabled) ? unitFill : placeFill;
    const stroke = (isUnpublished || isDisabled) ? unitStroke : placeStroke;

    const modifier = isInvisible
      ? ' place--invisible'
      : isUnpublished
        ? ' place--unpublished'
        : isDisabled
          ? ' place--disabled'
          : '';
    const className = `place${modifier}`;
    const id = attributes.id || '';

    // Build attribute string - stroke-width comes from CSS
    const attrParts = [];
    if (id) attrParts.push(`data-svg-id="${id}"`);
    attrParts.push(`class="${className}"`);
    attrParts.push(`fill="${fill}"`);
    attrParts.push(`stroke="${stroke}"`);

    // Add geometry attributes
    if (attributes.d) attrParts.push(`d="${attributes.d}"`);
    if (attributes.points) attrParts.push(`points="${attributes.points}"`);
    if (attributes.x !== undefined) attrParts.push(`x="${attributes.x}"`);
    if (attributes.y !== undefined) attrParts.push(`y="${attributes.y}"`);
    if (attributes.width !== undefined) attrParts.push(`width="${attributes.width}"`);
    if (attributes.height !== undefined) attrParts.push(`height="${attributes.height}"`);
    if (attributes.r !== undefined) attrParts.push(`r="${attributes.r}"`);
    if (attributes.cx !== undefined) attrParts.push(`cx="${attributes.cx}"`);
    if (attributes.cy !== undefined) attrParts.push(`cy="${attributes.cy}"`);
    if (attributes.transform) attrParts.push(`transform="${attributes.transform}"`);

    return `<${type} ${attrParts.join(' ')} />`;
  });

  return shapeStrings.join('');
};

/**
 * Builds props object for place SVG element
 * @param {Object} params - Parameters for building place props
 * @param {string} params.id - SVG element ID
 * @param {boolean} params.isInvisible - Whether place should be invisible
 * @param {string} params.placeStroke - Stroke color for place
 * @param {string} params.placeFill - Fill color for place
 * @param {Object} params.attributes - SVG attributes (points, d, x, y, width, height, r, cx, cy, transform)
 * @returns {Object} Props object for SVG element
 */
export const buildPlaceProps = ({
  id,
  isInvisible,
  placeStroke,
  placeFill,
  attributes,
}) => {
  const svgAttributes = extractSvgAttributes(attributes);

  const props = {
    'data-svg-id': id,
    stroke: isInvisible ? 'transparent' : placeStroke,
    fill: isInvisible ? 'transparent' : placeFill,
    className: !isInvisible ? 'place' : '',
  };

  // Add only defined SVG attributes
  // eslint-disable-next-line no-restricted-syntax
  for (const property in svgAttributes) {
    if (svgAttributes.hasOwnProperty(property)) {
      props[property] = svgAttributes[property];
    }
  }

  return props;
};

/**
 * Builds modal header data for place detail modal
 * @param {boolean} personalizationActive - Whether personalization is active
 * @param {string} uuid - Place UUID
 * @param {Function} FavoriteButton - FavoriteButton component
 * @returns {Object} Modal header data object
 */
export const buildPlaceModalHeaderData = (personalizationActive, uuid, FavoriteButton) => {
  const components = getFavoriteComponents(personalizationActive, 'place', uuid, FavoriteButton);

  return {
    titleParts: [],
    tags: [],
    subtitles: [],
    btns: [],
    components,
  };
};

/**
 * Determines if a place marker should show as a link
 * @param {Object} pin - Place pin data
 * @returns {boolean} Whether place is a link
 */
// Use truthy check since Drupal may send "true" or "1" as strings
export const isPlaceLink = (pin) => !!pin?.create_modal;

/**
 * Determines if a place should be invisible
 * @param {Object} pin - Place pin data
 * @returns {boolean} Whether place is invisible
 */
export const isPlaceInvisible = (pin) => pin?.pin_type === 'Invisible SVG';

/**
 * Gets label orientation with fallback
 * @param {Object} pin - Place pin data
 * @returns {string} Label orientation ('left', 'right', 'top', 'bottom')
 */
export const getLabelOrientation = (pin) => getPropertyOrFallback(pin, 'label_orientation', 'left');

/**
 * Gets modal title with fallback
 * @param {Object} pin - Place pin data
 * @returns {string} Modal title
 */
export const getModalTitle = (pin) => getPropertyOrFallback(pin, 'modal_title', '');

